import { dataLocalKeyProviderConst } from "constants/common/providerConst";

const isCryptoAvailable =
  typeof window !== "undefined" && window.crypto?.subtle;

// 문자 암호화 함수
export const encryptText = async (data, password) => {
  if (!isCryptoAvailable) return JSON.stringify(data);

  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const key = await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      iterations: 100000,
      salt: salt,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"]
  );

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    enc.encode(data)
  );

  // 모든 데이터를 Base64 문자열로 변환하고 결합하여 하나의 문자열로 반환
  const ciphertext = btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  const saltString = btoa(String.fromCharCode(...salt));
  const ivString = btoa(String.fromCharCode(...iv));

  // 구분자로 ':'를 사용하여 결합
  return JSON.stringify(`${ciphertext}:${saltString}:${ivString}`);
};
// 문자 복호화 함수
export const decryptText = async (encryptedString, password) => {
  if (!isCryptoAvailable) return JSON.parse(encryptedString);

  const enc = new TextEncoder();
  const dec = new TextDecoder();

  const [ciphertext, saltString, ivString] =
    JSON.parse(encryptedString).split(":");

  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  const salt = Uint8Array.from(
    atob(saltString)
      .split("")
      .map((c) => c.charCodeAt(0))
  );
  const key = await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      iterations: 100000,
      salt: salt,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["decrypt"]
  );

  const iv = Uint8Array.from(
    atob(ivString)
      .split("")
      .map((c) => c.charCodeAt(0))
  );
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    Uint8Array.from(
      atob(ciphertext)
        .split("")
        .map((c) => c.charCodeAt(0))
    )
  );

  return dec.decode(decrypted);
};
const { accessToken, email, page, sns, role, nickname } = dataLocalKeyProviderConst;
export const TokenManager = {
  token: {
    access: accessToken.key,
    email: email.key,
    role: role.key,
    nickname: nickname.key,
  },
  saveAccessToken: async (token, emailValue, roleValue, nicknameValue) => {
    const isProd = process.env.REACT_APP_TYPE === "prod";
    const expiryDuration = isProd 
      ? 1 * 24 * 60 * 60 * 1000 // 상용: 1일 (refresh 기준)
      : 7 * 24 * 60 * 60 * 1000; // 개발: 1주일 (refresh 기준)
    const expiryTime = Date.now() + expiryDuration;
    localStorage.setItem(TokenManager.token.access, `${token}::${expiryTime}`);
    localStorage.setItem(
      TokenManager.token.email,
      await encryptText(emailValue, email.password)
    );
    localStorage.setItem(
      TokenManager.token.role,
      await encryptText(roleValue, role.password)
    );
    if (nicknameValue) {
      localStorage.setItem(
        TokenManager.token.nickname,
        await encryptText(nicknameValue, nickname.password)
      );
    }
  },
  saveNickname: async (nicknameValue) => {
    if (nicknameValue) {
      localStorage.setItem(
        TokenManager.token.nickname,
        await encryptText(nicknameValue, nickname.password)
      );
    } else {
      localStorage.removeItem(TokenManager.token.nickname);
    }
  },
  getAccessToken: async () => {
    const combinedExpiry = localStorage.getItem(TokenManager.token.access);
    if (!combinedExpiry) return null;

    const [token, expiryTime] = combinedExpiry.split("::");
    if (Date.now() > Number(expiryTime)) {
      TokenManager.clearAccessToken();
      return null;
    }
    return {
      token,
      email: await decryptText(
        localStorage.getItem(TokenManager.token.email),
        email.password
      ),
      role: await decryptText(
        localStorage.getItem(TokenManager.token.role),
        role.password
      ),
      nickname: localStorage.getItem(TokenManager.token.nickname) ? await decryptText(
        localStorage.getItem(TokenManager.token.nickname),
        nickname.password
      ) : "",
    };
  },
  clearAccessToken: () => {
    localStorage.removeItem(TokenManager.token.access); // Access Token 삭제
    localStorage.removeItem(TokenManager.token.email);
    localStorage.removeItem(TokenManager.token.role);
    localStorage.removeItem(TokenManager.token.nickname);
  },
};

export const PageManager = {
  name: page.key,
  savePageLocal: (pageUrl) => {
    localStorage.setItem(PageManager.name, pageUrl);
  },

  getPageLocal: () => {
    return localStorage.getItem(PageManager.name);
  },

  clearPageLocal: () => {
    localStorage.removeItem(PageManager.name);
  },
};

export const dataLocalManager = {
  saveInfo: async ({ snsValue }) => {
    return { sns: await encryptText(snsValue, sns.password) };
  },
  getInfo: async ({ snsValue }) => {
    return { sns: await decryptText(snsValue, sns.password) };
  },
};
